'use client';

import dynamic from 'next/dynamic';
import { useCallback, useEffect, useRef, useState } from 'react';
import useUser from '@alltrails/context/hooks/useUser';
import useIsVisible from '@alltrails/core/hooks/useIsVisible';
import Button from '@alltrails/denali/components/Button';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import CollParks from '@alltrails/denali/icons/CollParks';
import useBaseBranchUrl from '@alltrails/install-prompt/hooks/useBaseBranchUrl';
import type { User } from '@alltrails/types/User';
import SectionHeader from '../SectionHeader';
import styles from './DownloadAppModule.module.scss';

const VIMEO_ORIGIN = 'https://player.vimeo.com';
const VIDEO_SRC = `${VIMEO_ORIGIN}/video/952401576?h=cb37bede0d&badge=0&muted=1&loop=1&autopause=0&player_id=0&app_id=58479&unmute_button=1&api=1`;

const DownloadOrOpenInTheAppModal = dynamic(() => import('@alltrails/in-app/components/DownloadOrOpenInTheAppModal/DownloadOrOpenInTheAppModal'), {
  ssr: false
});
const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

type Props = {
  parkId: number;
};

let eventTriggeredBySystem = false;

const DownloadAppModule = ({ parkId }: Props) => {
  // *** This component is only shown to en locale ***
  const videoRef = useRef<HTMLIFrameElement>(null);
  const userHasPausedRef = useRef(false);
  const isVisible = useIsVisible(videoRef, { threshold: 0.5 });
  const user = useUser();
  const isMobile = useIsMobileSizedScreen();
  const deeplinkPath = `alltrails://parks/${parkId}`;
  const baseBranchUrl = useBaseBranchUrl();
  const deeplink = `${baseBranchUrl}&$ios_deeplink_path=${deeplinkPath}&$android_deeplink_path=${deeplinkPath}`;
  const [isSignupModalOpen, toggleSignupModal] = useState(false);
  const [isHitTheTrailModalOpen, setIsHitTheTrailModalOpen] = useState(false);

  const handleClickOpenInApp = useCallback(
    (currentUser?: User | null) => {
      if (!currentUser && !user) {
        toggleSignupModal(true);
        return;
      }

      if (isMobile) {
        window.location.assign(deeplink);
      } else {
        setIsHitTheTrailModalOpen(true);
      }
    },
    [isMobile, deeplink, user]
  );

  useEffect(() => {
    // we're using postMessage to avoid bundle size issues with the Vimeo player @vimeo/player package
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== VIMEO_ORIGIN) {
        return;
      }

      const data = JSON.parse(event.data) as { event?: string; value?: string };

      if (data.event === 'ready') {
        const iframe = videoRef.current!;
        iframe.contentWindow!.postMessage(JSON.stringify({ method: 'addEventListener', value: 'play' }), VIMEO_ORIGIN);
        iframe.contentWindow!.postMessage(JSON.stringify({ method: 'addEventListener', value: 'pause' }), VIMEO_ORIGIN);
        return;
      }

      if (data.event === 'pause' && eventTriggeredBySystem) {
        eventTriggeredBySystem = false;
        return;
      }

      if (data.event === 'play' && eventTriggeredBySystem) {
        eventTriggeredBySystem = false;
        return;
      }

      // the user has paused the video
      if (data.event === 'pause') {
        userHasPausedRef.current = true;
      }

      // the user has played the video
      if (data.event === 'play') {
        userHasPausedRef.current = false;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    const iframe = videoRef.current;

    if (!iframe?.contentWindow || userHasPausedRef.current) {
      return;
    }

    eventTriggeredBySystem = true;

    if (isVisible) {
      iframe.contentWindow.postMessage(JSON.stringify({ method: 'play' }), VIMEO_ORIGIN);
    } else {
      iframe.contentWindow.postMessage(JSON.stringify({ method: 'pause' }), VIMEO_ORIGIN);
    }
  }, [isVisible]);

  return (
    <>
      <section className={styles.downloadAppModuleSSection}>
        <div className={styles.titleSection}>
          <div className={styles.parks}>
            <CollParks size="sm" />
            Parks
          </div>
          <SectionHeader title="Headed to a park?" className={styles.title} />
          <SectionHeader title="Take AllTrails with you" className={styles.subtitle} />
          <Button className={styles.desktopButton} testId="open-in-app" text="Open in app" onClick={() => handleClickOpenInApp()} />
        </div>
        <div className={styles.movie}>
          <div className={styles.embedFlex}>
            <div className={styles.embedContainer}>
              <iframe
                ref={videoRef}
                src={VIDEO_SRC}
                frameBorder="0"
                allow="fullscreen; picture-in-picture;"
                className={styles.embedVid}
                title="Parks Open in App"
                loading="lazy"
              />
            </div>
          </div>
        </div>
        <Button className={styles.mobileButton} testId="open-in-app-mobile" text="Open in app" onClick={() => handleClickOpenInApp()} />
      </section>
      {isHitTheTrailModalOpen && (
        <DownloadOrOpenInTheAppModal
          deeplink="open"
          isOpen={isHitTheTrailModalOpen}
          onCloseRequest={() => setIsHitTheTrailModalOpen(false)}
          subtitle="Scan the QR code to open  in the app"
          title="Open in the app"
          resourceType="park"
          resourceId={parkId}
          webToAppAction="open in app"
        />
      )}
      {isSignupModalOpen && (
        <SignUpModal
          closeModal={() => toggleSignupModal(false)}
          isOpen={isSignupModalOpen}
          onSuccess={(userAfterSignUp: User) => {
            toggleSignupModal(false);
            handleClickOpenInApp(userAfterSignUp);
          }}
          trigger="Park page app promo module"
          heading="Create your free account"
          upsellTrigger="Park page app promo module"
        />
      )}
    </>
  );
};

export default DownloadAppModule;
